/* @flow */

import * as React from 'react';

import type { KeyValuePair } from '../jsHelpers/types';

import { logError } from '../debug/debug';
import { APPLICATION_ID } from './types';

// Map raw application ID string directly to physical workspace package folder layout name
const getActiveBrandPackageName = () => {
  const brandPackageMap = {
    [APPLICATION_ID.FranceChannel]: 'francechannel',
    [APPLICATION_ID.Gaumont]: 'gaumont',
    [APPLICATION_ID.GaumontVIP]: 'gaumontvip',
    [APPLICATION_ID.MyVideofutur]: 'myvideofutur',
    [APPLICATION_ID.Post]: 'posttvgo',
    [APPLICATION_ID.Premiere]: 'premiere',
    [APPLICATION_ID.Vialis]: 'vialis',
    [APPLICATION_ID.Viva]: 'viva',
    [APPLICATION_ID.WiBox]: 'wibox',
    [APPLICATION_ID.ZeopTV]: 'zeoptv',
  };

  return brandPackageMap[process.env.REACT_APP_ID] || 'myvideofutur';
};

const activePackage = getActiveBrandPackageName();

const getAppLottieLoader: () => any = () => {
  try {
    // $FlowFixMe[unsupported-syntax]
    return require(`@ntg/${activePackage}/dist/assets/lottie-loader.json`);
  } catch (error) {
    logError(`Cannot load Lottie loader: ${error.message}`);
    return null;
  }
};

const createAppHeaderLogo: () => string | null = () => {
  try {
    // $FlowFixMe[unsupported-syntax]
    return require(`@ntg/${activePackage}/dist/assets/images/header-logo.svg`);
  } catch (error) {
    logError(`Cannot load header logo: ${error.message}`);
    return null;
  }
};

const createAppFooterLogo: () => string | null = () => {
  try {
    // $FlowFixMe[unsupported-syntax]
    return require(`@ntg/${activePackage}/dist/assets/images/footer-logo.svg`);
  } catch (error) {
    logError(`Cannot load footer logo: ${error.message}`);
    return null;
  }
};

const createAppMobileAppLogo: () => string | null = () => {
  try {
    // $FlowFixMe[unsupported-syntax]
    return require(`@ntg/${activePackage}/dist/assets/images/mobile-app-logo.svg`);
  } catch (error) {
    logError(`Cannot load mobile app logo: ${error.message}`);
    return null;
  }
};

const createAppAppleStoreLogo: () => string | null = () => {
  try {
    // $FlowFixMe[unsupported-syntax]
    return require(`@ntg/${activePackage}/dist/assets/images/store-logo-apple.svg`);
  } catch (error) {
    logError(`Cannot load Apple Store logo: ${error.message}`);
    return null;
  }
};

const createAppGoogleStoreLogo: () => string | null = () => {
  try {
    // $FlowFixMe[unsupported-syntax]
    return require(`@ntg/${activePackage}/dist/assets/images/store-logo-google.svg`);
  } catch (error) {
    logError(`Cannot load Google Store logo: ${error.message}`);
    return null;
  }
};

const createAppBackgroundImage: () => string | null = () => {
  try {
    // $FlowFixMe[unsupported-syntax]
    return require(`@ntg/${activePackage}/dist/assets/images/tiled-background.jpg`);
  } catch (error) {
    logError(`Cannot load background image: ${error.message}`);
    return null;
  }
};

const getAppCss: () => Promise<any> = () => {
  if (!activePackage) {
    return Promise.resolve();
  }
  // $FlowFixMe[unsupported-syntax]
  return import(
    /* rspackMode: "lazy" */
    /* rspackInclude: /themes\/base\.css$/ */
    `@ntg/${activePackage}/dist/themes/base.css`
  );
};

const createAppHasHeaderCloseButton: () => boolean = () => {
  switch (process.env.REACT_APP_ID) {
    case APPLICATION_ID.Gaumont:
    case APPLICATION_ID.Premiere:
    case APPLICATION_ID.Post:
    case APPLICATION_ID.Viva:
      return true;
    case APPLICATION_ID.FranceChannel:
    case APPLICATION_ID.GaumontVIP:
    case APPLICATION_ID.MyVideofutur:
    case APPLICATION_ID.Vialis:
    case APPLICATION_ID.WiBox:
    case APPLICATION_ID.ZeopTV:
    default:
      return false;
  }
};

// Firebase configurations come from Firebase Console projects' settings
const createFirebaseConfig: () => KeyValuePair<string> = () => {
  switch (process.env.REACT_APP_ID) {
    case APPLICATION_ID.Vialis:
      return {
        apiKey: 'AIzaSyAp15rpKXPBnT8YH5nEj-2rz2AKaUKD_Mw',
        appId: '1:499868816453:web:38c49438491dc127febf2c',
        authDomain: 'vialis-73363.firebaseapp.com',
        measurementId: 'G-K7WP3LLLC2',
        messagingSenderId: '499868816453',
        projectId: 'vialis-73363',
        storageBucket: 'vialis-73363.firebasestorage.app',
      };

    case APPLICATION_ID.FranceChannel:
    case APPLICATION_ID.Gaumont:
    case APPLICATION_ID.GaumontVIP:
    case APPLICATION_ID.MyVideofutur:
    case APPLICATION_ID.Premiere:
    case APPLICATION_ID.Post:
    case APPLICATION_ID.Viva:
    case APPLICATION_ID.WiBox:
    case APPLICATION_ID.ZeopTV:
    default:
      return {};
  }
};

// Firebase VAPID keys come from Firebase Console, in FCM settings
const createFirebaseVapidKey: () => string = () => {
  switch (process.env.REACT_APP_ID) {
    case APPLICATION_ID.Vialis:
      return 'BNM1Lzav0T_zIkz4E2A53Q1uIu9fFVWfW0T9O6pKiK-YTG_LO5zd9l-3SaPVMndAAoIvv894WTL-D3C2iahrXYs';

    case APPLICATION_ID.FranceChannel:
    case APPLICATION_ID.Gaumont:
    case APPLICATION_ID.GaumontVIP:
    case APPLICATION_ID.MyVideofutur:
    case APPLICATION_ID.Premiere:
    case APPLICATION_ID.Post:
    case APPLICATION_ID.Viva:
    case APPLICATION_ID.WiBox:
    case APPLICATION_ID.ZeopTV:
    default:
      return '';
  }
};

type GenericFooterContentProps = { ... };

const AppFooterContent: React.ComponentType<GenericFooterContentProps> = React.lazy((): Promise<{ default: React.ComponentType<GenericFooterContentProps> }> => {
  if (!activePackage) {
    return Promise.resolve({ default: () => null });
  }

  // $FlowFixMe[unsupported-syntax]
  return import(
    /* rspackMode: "lazy" */
    /* rspackInclude: /components\/footer\/FooterContent$/ */
    `@ntg/${activePackage}/dist/components/footer/FooterContent`
  )
    .then((module) => {
      return {
        default: (module.default ?? module.FooterContent) as React.ComponentType<GenericFooterContentProps>,
      };
    })
    .catch(() =>
      // $FlowFixMe[unsupported-syntax]
      import(`@ntg/${activePackage}/dist`).then((module) => {
        return {
          default: (module.default ?? module.FooterContent) as React.ComponentType<GenericFooterContentProps>,
        };
      }),
    );
});

// $FlowFixMe[unsupported-syntax]
export const AppProfilesBackgroundImage: string = require(`@ntg/${activePackage}/dist/assets/images/profiles-background.jpg`) as string;

const AppAppleStoreLogo: string | null = createAppAppleStoreLogo();
const AppGoogleStoreLogo: string | null = createAppGoogleStoreLogo();
const AppHasHeaderCloseButton: boolean = createAppHasHeaderCloseButton();
const AppHeaderLogo: string | null = createAppHeaderLogo();
const AppFooterLogo: string | null = createAppFooterLogo();
const AppMobileAppLogo: string | null = createAppMobileAppLogo();
const AppBackgroundImage: string | null = createAppBackgroundImage();
const firebaseConfig: KeyValuePair<string> = createFirebaseConfig();
const firebaseVapidKey: string | null = createFirebaseVapidKey();

export {
  AppAppleStoreLogo,
  AppBackgroundImage,
  AppFooterContent,
  AppFooterLogo,
  AppGoogleStoreLogo,
  AppHasHeaderCloseButton,
  AppHeaderLogo,
  AppMobileAppLogo,
  firebaseConfig,
  firebaseVapidKey,
  getAppCss,
  getAppLottieLoader,
};
